import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, true/false = determined
  const [isLoading, setIsLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  
  const checkAuth = async () => {
    try {
      // Dispara as duas chamadas em paralelo — economiza ~400 ms de latência em rede
      const [authRes, subRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/subscriptions/check-access`, { withCredentials: true })
          .catch(() => ({ data: { has_access: false } })),
      ]);
      setUser(authRes.data);
      setIsAuthenticated(true);
      setHasSubscription(subRes.data.has_access);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      setHasSubscription(false);
    } finally {
      setIsLoading(false);
    }
  };
  
  const checkSubscription = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions/check-access`, {
        withCredentials: true
      });
      setHasSubscription(response.data.has_access);
      return response.data;
    } catch (error) {
      setHasSubscription(false);
      return { has_access: false, reason: 'error' };
    }
  };
  
  const refreshUser = async () => {
    await checkAuth();
  };
  
  const login = async (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setIsLoading(false);
    // Check subscription immediately so SubscriptionRequired guard works right away
    await checkSubscription();
  };
  
  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('sb_session');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      setIsAuthenticated(false);
      setHasSubscription(false);
    }
  };
  
  const updateUser = (userData) => {
    setUser(userData);
  };
  
  useEffect(() => {
    // Restore Bearer token from localStorage (for magic-link sessions)
    const stored = localStorage.getItem('sb_session');
    if (stored) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${stored}`;
    }
    checkAuth();

    // Interceptor global: qualquer resposta 401 fora do próprio checkAuth/login
    // invalida o estado de autenticação e redireciona para login.
    const interceptorId = axios.interceptors.response.use(
      res => res,
      err => {
        const status = err?.response?.status;
        const url = err?.config?.url || '';
        const isAuthEndpoint =
          url.includes('/auth/me') ||
          url.includes('/auth/login') ||
          url.includes('/auth/firebase-session') ||
          url.includes('/auth/magic-login') ||
          url.includes('/auth/onboarding') ||
          url.includes('/auth/assessment') ||
          url.includes('/auth/complete-assessment') ||
          url.includes('/auth/analyze-assessment') ||
          url.includes('/auth/redo-assessment') ||
          url.includes('/subscriptions/check-access');

        if (status === 401 && !isAuthEndpoint) {
          // Sessão expirou durante o uso — limpa estado e manda para login
          localStorage.removeItem('sb_session');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          setIsAuthenticated(false);
          setHasSubscription(false);
          window.location.href = '/login';
        }
        return Promise.reject(err);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptorId);
    };
  }, []); // eslint-disable-line
  
  const value = {
    user,
    isAuthenticated,
    isLoading,
    hasSubscription,
    login,
    logout,
    updateUser,
    checkAuth,
    refreshUser,
    checkSubscription
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
